// Dependencies use the same source identities as the saved calculation graph.
export function equipmentReferences(value, references = [], skipSelf = false) {
    if (!value || typeof value !== 'object') return references;
    if (typeof value.AccessoryDetail === 'string') equipmentReferences(JSON.parse(value.AccessoryDetail), references, true);
    if (!skipSelf && value.Source === 'CUSTOM' && value.SourceDatabaseId !== undefined) {
        references.push({ id: String(value.SourceDatabaseId), kind: value.AccessoryType });
    }
    for (const child of Object.values(value)) equipmentReferences(child, references);
    return references;
}

export function syncJobBlockReason(job, jobs, saveEquipmentCode, saveOfferCode) {
    if (!job?.Operation?.Code) return 'malformed-work';
    if (job.NeedsSaveConfirmation) return 'paused';
    if(job.RetainWork&&job.Operation.Code===saveOfferCode){
        const alias=String(job.ExistingId||job.OriginalLocalId||'offerId_'+job.TimeStamp);
        const earlier=jobs.find(other=>other!==job&&other.RetainWork&&other.Operation?.Code===saveOfferCode&&other.TimeStamp<job.TimeStamp&&
            [String(other.ExistingId||'offerId_'+other.TimeStamp),String(other.OriginalLocalId)].includes(alias));
        if(earlier)return 'calculation';
        if(alias.startsWith('offerId_')&&alias!=='offerId_'+job.TimeStamp)return 'missing-calculation';
    }
    if (job.Operation.Code === saveEquipmentCode && job.ExistingId) {
        const prior = jobs.find(other => other !== job && other.Operation?.Code === saveEquipmentCode &&
            other.Details?.AccessoryType === job.Details?.AccessoryType &&
            (String(other.ExistingId || 'accessoryId_' + other.TimeStamp) === String(job.ExistingId) ||
             String(other.OriginalLocalId) === String(job.ExistingId)) && other.TimeStamp < job.TimeStamp);
        if (prior) return 'equipment';
        if (String(job.ExistingId).startsWith('accessoryId_')) return 'missing-equipment';
    }
    let references;
    try { references = equipmentReferences(job.Details); } catch (_) { return 'malformed-work'; }
    for (const reference of references) {
        const producer = jobs.find(other => other !== job && other.Operation?.Code === saveEquipmentCode &&
            [String(other.ExistingId || 'accessoryId_' + other.TimeStamp), String(other.OriginalLocalId)].includes(reference.id) &&
            (!reference.kind || other.Details?.AccessoryType === reference.kind));
        if (producer) return 'equipment';
        if (reference.id.startsWith('accessoryId_')) return 'missing-equipment';
    }
    return null;
}

export function syncJobDescription(job) {
    try {
        return job.Details?.Offer?.Description || job.Details?.Description ||
            (job.Details?.AccessoryDetail ? JSON.parse(job.Details.AccessoryDetail).Description : '') || 'Equipment draft';
    } catch (_) { return 'Equipment draft requires recovery'; }
}

export function replaceEquipmentReference(value, oldId, newId, kind) {
    if (!value || typeof value !== 'object') return;
    if (typeof value.AccessoryDetail === 'string') {
        const detail = JSON.parse(value.AccessoryDetail);
        replaceEquipmentReference(detail, oldId, newId, kind);
        value.AccessoryDetail = JSON.stringify(detail);
    }
    if (value.Source === 'CUSTOM' && String(value.SourceDatabaseId) === String(oldId) &&
        (!value.AccessoryType || value.AccessoryType === kind)) value.SourceDatabaseId = newId;
    for (const child of Object.values(value)) replaceEquipmentReference(child, oldId, newId, kind);
}

// Updated-app identity receipts survive an interrupted local completion. They
// identify saved work; the server remains the authority for every library write.
export function resolveEquipmentIdentity(entries, kind, id) {
    let current=String(id),found;const seen=new Set();
    for(let step=0;step<32;step++){
        if(seen.has(current))throw new Error('Local equipment identity cycle');seen.add(current);
        const next=entries.find(entry=>entry.kind===kind&&String(entry.from)===current);
        if(!next)break;found=next;
        if(String(next.to)===current)break;current=String(next.to);
        if(step===31)throw new Error('Local equipment identity chain is too long');
    }
    if(!found)return null;
    const latest=entries.find(entry=>entry.kind===kind&&String(entry.from)===String(found.to));
    return {...found,updateCounter:Math.max(found.updateCounter,latest?.updateCounter||0)};
}

export function recordEquipmentIdentity(entries, kind, from, id, updateCounter, revision) {
    if(!['Body','Crane','Other','Fridge','Taillift','FifthWheel','Hitch','Hooklift','Trailer','Payload','Calculation'].includes(kind)||
        !Number.isInteger(id)||id<=0||!Number.isFinite(updateCounter)||updateCounter<0||!Number.isFinite(revision))throw new Error('Invalid equipment acknowledgement');
    const next=entries.map(entry=>({...entry}));
    for(const alias of new Set([String(from),String(id)])){
        const prior=next.find(entry=>entry.kind===kind&&String(entry.from)===alias);
        if(prior&&prior.revision>revision)continue;
        const value={kind,from:alias,to:id,updateCounter:Math.max(updateCounter,prior?.updateCounter||0),revision};
        if(prior)Object.assign(prior,value);else next.push(value);
    }
    return next;
}

export function applyEquipmentIdentityMappings(value, entries) {
    for(const entry of entries){
        if(entry.kind==='Calculation')continue;
        const resolved=resolveEquipmentIdentity(entries,entry.kind,entry.from);
        if(String(entry.from)!==String(resolved.to))replaceEquipmentReference(value,entry.from,resolved.to,entry.kind);
    }
    return value;
}

export function applyEquipmentMappingsToJob(job, entries, saveEquipmentCode, saveOfferCode) {
    if(!job.RetainWork)return job;
    applyEquipmentIdentityMappings(job.Details,entries);
    if(job.Operation?.Code===saveEquipmentCode){
        const kind=job.Details.AccessoryType,alias=job.ExistingId||job.OriginalLocalId||'accessoryId_'+job.TimeStamp;
        const mapping=resolveEquipmentIdentity(entries,kind,alias);
        if(mapping){
            const detail=JSON.parse(job.Details.AccessoryDetail);detail.Id=mapping.to;
            detail.UpdateCounter=mapping.updateCounter;
            job.Details.AccessoryDetail=JSON.stringify(detail);job.ExistingId=mapping.to;job.Params.id=mapping.to;
        }
    }else if(job.Operation?.Code===saveOfferCode){
        const alias=job.ExistingId||job.OriginalLocalId||'offerId_'+job.TimeStamp,mapping=resolveEquipmentIdentity(entries,'Calculation',alias);
        if(mapping){
            job.OriginalLocalId=job.OriginalLocalId||String(alias);job.ExistingId=mapping.to;job.Params.offerId=mapping.to;
            job.Details.Offer.Id=mapping.to;job.Details.Offer.UpdateCounter=mapping.updateCounter;
        }else if(!(job.Details.Offer.Id>0))job.Details.Offer.Id=0;
    }
    return job;
}
